// --- src/components/UpdatePassword.tsx ---
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Lock, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Toast } from './Toast';

const UpdatePassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Check if user has a valid session (from password reset link)
  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        // If no session, redirect to login after a moment
        setTimeout(() => {
          window.location.hash = '';
          window.location.reload();
        }, 2000);
        setToast({
          message: 'Érvénytelen vagy lejárt link. Átirányítás a bejelentkezéshez...',
          type: 'error',
        });
      }
    };

    checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (newPassword.length < 6) {
      setToast({ message: 'A jelszónak legalább 6 karakter hosszúnak kell lennie', type: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({ message: 'A jelszavak nem egyeznek', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setSuccess(true);
      setToast({ message: 'Jelszó sikeresen megváltoztatva! Átirányítás...', type: 'success' });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        window.location.hash = '';
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      setToast({
        message: error.message || 'Hiba a jelszó megváltoztatásakor',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: 'var(--color-background, #f8fafc)' }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-gray-700 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="text-green-600 dark:text-green-400" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Jelszó sikeresen megváltoztatva!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Átirányítás a bejelentkezéshez...</p>
          <div className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400">
            <Loader2 className="animate-spin" size={20} />
            <span className="text-sm">Betöltés...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-gray-700">
        <div className="bg-slate-900 dark:bg-gray-950 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-blue-900/50">
            <Lock className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Új jelszó beállítása</h1>
          <p className="text-slate-400 text-sm mt-2">Add meg az új jelszavad</p>
        </div>

        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                Új jelszó
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-3 text-slate-400 dark:text-gray-500"
                  size={18}
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                Jelszó megerősítése
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-3 text-slate-400 dark:text-gray-500"
                  size={18}
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  <span>Mentés...</span>
                </>
              ) : (
                <>
                  <Lock size={18} />
                  <span>Jelszó mentése</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default UpdatePassword;
